// services/shop-analytics.service.ts
// ✅ COMPLETE with Engagement Analytics + Session Duration Distribution

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, tap, of } from 'rxjs';
import { environment } from 'src/environments/environment';

// ========================================
// EXISTING INTERFACES (Sales Analytics)
// ========================================

export interface ShopAnalytics {
  totalRevenue: number;
  totalOrders: number;
  averageOrderValue: number;
  conversionRate: number;
  period: string;
  trends?: {
    revenue: number;
    orders: number;
  };
}

export interface ShopMetrics {
  revenue: Array<{ date: string; value: number }>;
  orders: Array<{ date: string; value: number }>;
  period: string;
}

export interface TopProduct {
  id: number;
  name: string;
  imageUrl?: string;
  revenue: number;
  orderCount: number;
  totalQuantity: number;
}

export interface RecentOrder {
  id: number;
  orderNumber: string;
  customerName: string;
  customerEmail: string;
  totalPrice: number;
  status: string;
  paymentStatus: string;
  deliveryMethod: string;
  createdAt: string;
  items: any[];
}

export interface LowStockProduct {
  id: number;
  name: string;
  sku: string;
  stockQuantity: number;
  imageUrl?: string;
  price: number;
  category: string;
}

export interface PayoutSummary {
  hasStripeAccount: boolean;
  availableBalance: number;
  pendingBalance?: number;
  nextPayoutEstimate: number;
  thisMonthRevenue?: number;
}

export interface PayoutChartData {
  historical: Array<{
    month: string;
    revenue: number;
    commission: number;
    stripeFees: number;
    netPayout: number;
  }>;
  forecast: Array<{
    month: string;
    revenue: number;
    commission: number;
    stripeFees: number;
    netPayout: number;
    isForecast: boolean;
  }>;
}

// ========================================
// NEW INTERFACES (Engagement Analytics)
// ========================================

export interface SessionDurationBucket {
  bucket: string;
  count: number;
  percentage: number;
}

export interface EngagementAnalytics {
  profileVisits: {
    total: number;
    trend: number;
  };
  uniqueVisitors: number;
  mostViewedProducts: Array<{
    productId: number;
    productName: string;
    imageUrl?: string;
    viewCount: number;
  }>;
  addToCartRate: number;
  averageSessionDuration: number;
  sessionDurationDistribution: SessionDurationBucket[]; // ✅ NEW
  engagementChart: Array<{
    date: string;
    events: number;
  }>;
  productViewCount: number;
  addToCartCount: number;
  period: string;
}

export interface ProductEngagement {
  product: {
    id: number;
    name: string;
    imageUrl?: string;
  };
  views: number;
  addToCart: number;
  conversionRate: number;
  viewsChart: Array<{
    date: string;
    views: number;
  }>;
  period: string;
}

// ========================================
// TICKET ANALYTICS INTERFACES
// ========================================

export interface TicketAnalytics {
  totalSold: number;
  totalRedemptions: number;
  byType: {
    stamp_card: number;
    event_ticket: number;
    membership: number;
  };
  byStatus: {
    active: number;
    expired: number;
    exhausted: number;
  };
}

// Cache Entry Interface
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  period?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ShopAnalyticsService {
  private baseUrl = `${environment.apiUrl}/business/shop/analytics`;
  
  // Cache subjects for real-time updates
  private analyticsCache$ = new BehaviorSubject<ShopAnalytics | null>(null);
  private metricsCache$ = new BehaviorSubject<ShopMetrics | null>(null);
  private payoutSummaryCache$ = new BehaviorSubject<PayoutSummary | null>(null);
  private engagementCache$ = new BehaviorSubject<EngagementAnalytics | null>(null);
  private ticketAnalyticsCache$ = new BehaviorSubject<TicketAnalytics | null>(null);

  public analytics$ = this.analyticsCache$.asObservable();
  public metrics$ = this.metricsCache$.asObservable();
  public payoutSummary$ = this.payoutSummaryCache$.asObservable();
  public engagement$ = this.engagementCache$.asObservable();
  public ticketAnalytics$ = this.ticketAnalyticsCache$.asObservable();

  // Cache storage with TTL
  private cache = new Map<string, CacheEntry<any>>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  constructor(private http: HttpClient) {
  }

  /**
   * Check if cache entry is still valid
   */
  private isCacheValid(key: string, period?: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;

    const now = Date.now();
    const isExpired = (now - entry.timestamp) > this.CACHE_TTL;
    
    const periodMatches = !period || entry.period === period;

    const isValid = !isExpired && periodMatches;
   
    return isValid;
  }

  /**
   * Set cache entry
   */
  private setCache<T>(key: string, data: T, period?: string): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      period
    });
  }

  /**
   * Get cache entry
   */
  private getCache<T>(key: string): T | null {
    const entry = this.cache.get(key);
    return entry ? entry.data : null;
  }

  // ========================================
  // EXISTING METHODS (Sales Analytics)
  // ========================================

  getAnalytics(period: string = '30d', forceRefresh: boolean = false): Observable<ShopAnalytics> {
    const cacheKey = `analytics_${period}`;
    
    if (!forceRefresh && this.isCacheValid(cacheKey, period)) {
      const cached = this.getCache<ShopAnalytics>(cacheKey);
      if (cached) {
        this.analyticsCache$.next(cached);
        return of(cached);
      }
    }

    const params = new HttpParams().set('period', period);
    
    return this.http.get<ShopAnalytics>(`${this.baseUrl}/analytics`, { params }).pipe(
      tap(data => {
        this.setCache(cacheKey, data, period);
        this.analyticsCache$.next(data);
      })
    );
  }

  getMetrics(period: string = '30d', forceRefresh: boolean = false): Observable<ShopMetrics> {
    const cacheKey = `metrics_${period}`;
    
    if (!forceRefresh && this.isCacheValid(cacheKey, period)) {
      const cached = this.getCache<ShopMetrics>(cacheKey);
      if (cached) {
        this.metricsCache$.next(cached);
        return of(cached);
      }
    }

    const params = new HttpParams().set('period', period);
    
    return this.http.get<ShopMetrics>(`${this.baseUrl}/metrics`, { params }).pipe(
      tap(data => {
        this.setCache(cacheKey, data, period);
        this.metricsCache$.next(data);
      })
    );
  }

  getTopProducts(limit: number = 5, period: string = '30d', forceRefresh: boolean = false): Observable<{ products: TopProduct[]; period: string }> {
    const cacheKey = `top_products_${limit}_${period}`;
    
    if (!forceRefresh && this.isCacheValid(cacheKey, period)) {
      const cached = this.getCache<{ products: TopProduct[]; period: string }>(cacheKey);
      if (cached) return of(cached);
    }

    const params = new HttpParams()
      .set('limit', limit.toString())
      .set('period', period);
    
    return this.http.get<{ products: TopProduct[]; period: string }>(`${this.baseUrl}/top-products`, { params }).pipe(
      tap(data => this.setCache(cacheKey, data, period))
    );
  }

  getRecentOrders(limit: number = 10, forceRefresh: boolean = false): Observable<{ orders: RecentOrder[] }> {
    const cacheKey = `recent_orders_${limit}`;
    
    if (!forceRefresh && this.isCacheValid(cacheKey)) {
      const cached = this.getCache<{ orders: RecentOrder[] }>(cacheKey);
      if (cached) return of(cached);
    }

    const params = new HttpParams().set('limit', limit.toString());
    
    return this.http.get<{ orders: RecentOrder[] }>(`${this.baseUrl}/recent-orders`, { params }).pipe(
      tap(data => this.setCache(cacheKey, data))
    );
  }

  getLowStockProducts(threshold: number = 10, limit: number = 10, forceRefresh: boolean = false): Observable<{ products: LowStockProduct[]; threshold: number }> {
    const cacheKey = `low_stock_${threshold}_${limit}`;
    
    if (!forceRefresh && this.isCacheValid(cacheKey)) {
      const cached = this.getCache<{ products: LowStockProduct[]; threshold: number }>(cacheKey);
      if (cached) return of(cached);
    }

    const params = new HttpParams()
      .set('threshold', threshold.toString())
      .set('limit', limit.toString());
    
    return this.http.get<{ products: LowStockProduct[]; threshold: number }>(`${this.baseUrl}/low-stock`, { params }).pipe(
      tap(data => this.setCache(cacheKey, data))
    );
  }

  getPayoutSummary(forceRefresh: boolean = false): Observable<PayoutSummary> {
    const cacheKey = 'payout_summary';
    
    if (!forceRefresh && this.isCacheValid(cacheKey)) {
      const cached = this.getCache<PayoutSummary>(cacheKey);
      if (cached) {
        this.payoutSummaryCache$.next(cached);
        return of(cached);
      }
    }

    return this.http.get<PayoutSummary>(`${this.baseUrl}/payouts/summary`).pipe(
      tap(data => {
        this.setCache(cacheKey, data);
        this.payoutSummaryCache$.next(data);
      })
    );
  }

  getPayoutChartData(forceRefresh: boolean = false): Observable<PayoutChartData> {
    const cacheKey = 'payout_chart_data';
    
    if (!forceRefresh && this.isCacheValid(cacheKey)) {
      const cached = this.getCache<PayoutChartData>(cacheKey);
      if (cached) return of(cached);
    }

    return this.http.get<PayoutChartData>(`${this.baseUrl}/payouts/chart-data`).pipe(
      tap(data => this.setCache(cacheKey, data))
    );
  }

  // ========================================
  // NEW METHODS (Engagement Analytics)
  // ========================================

  /**
   * ✅ Get engagement analytics
   * @param period '7d' | '30d' | '90d'
   * @param forceRefresh Skip cache and force API call
   */
  getEngagementAnalytics(period: string = '30d', forceRefresh: boolean = false): Observable<EngagementAnalytics> {
    const cacheKey = `engagement_${period}`;
    
    if (!forceRefresh && this.isCacheValid(cacheKey, period)) {
      const cached = this.getCache<EngagementAnalytics>(cacheKey);
      if (cached) {
        this.engagementCache$.next(cached);
        return of(cached);
      }
    }

    const params = new HttpParams().set('period', period);
    
    return this.http.get<EngagementAnalytics>(`${this.baseUrl}/engagement`, { params }).pipe(
      tap(data => {
        this.setCache(cacheKey, data, period);
        this.engagementCache$.next(data);
      })
    );
  }

  /**
   * ✅ Get product-specific engagement analytics
   * @param productId Product ID
   * @param period '7d' | '30d' | '90d'
   * @param forceRefresh Skip cache and force API call
   */
  getProductEngagement(productId: number, period: string = '30d', forceRefresh: boolean = false): Observable<ProductEngagement> {
    const cacheKey = `product_engagement_${productId}_${period}`;

    if (!forceRefresh && this.isCacheValid(cacheKey, period)) {
      const cached = this.getCache<ProductEngagement>(cacheKey);
      if (cached) return of(cached);
    }

    const params = new HttpParams().set('period', period);

    return this.http.get<ProductEngagement>(`${this.baseUrl}/product-engagement/${productId}`, { params }).pipe(
      tap(data => this.setCache(cacheKey, data, period))
    );
  }

  // ========================================
  // TICKET ANALYTICS METHODS
  // ========================================

  /**
   * Get ticket analytics for shop overview
   * @param forceRefresh Skip cache and force API call
   */
  getTicketAnalytics(forceRefresh: boolean = false): Observable<{ analytics: TicketAnalytics }> {
    const cacheKey = 'ticket_analytics';

    if (!forceRefresh && this.isCacheValid(cacheKey)) {
      const cached = this.getCache<{ analytics: TicketAnalytics }>(cacheKey);
      if (cached) {
        this.ticketAnalyticsCache$.next(cached.analytics);
        return of(cached);
      }
    }

    return this.http.get<{ analytics: TicketAnalytics }>(`${environment.apiUrl}/business/tickets/analytics`).pipe(
      tap(data => {
        this.setCache(cacheKey, data);
        this.ticketAnalyticsCache$.next(data.analytics);
      })
    );
  }

  // ========================================
  // UTILITY METHODS
  // ========================================

  /**
   * Refresh all analytics data
   */
  refreshAllData(period: string = '30d'): void {
    this.getAnalytics(period, true).subscribe();
    this.getMetrics(period, true).subscribe();
    this.getPayoutSummary(true).subscribe();
    this.getEngagementAnalytics(period, true).subscribe();
  }

  /**
   * Invalidate specific cache entry
   */
  invalidateCache(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Invalidate cache for specific period
   */
  invalidatePeriod(period: string): void {
    const keysToDelete: string[] = [];
    
    this.cache.forEach((_, key) => {
      if (key.includes(period)) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Clear all cache
   */
  clearCache(): void {
    this.cache.clear();
    this.analyticsCache$.next(null);
    this.metricsCache$.next(null);
    this.payoutSummaryCache$.next(null);
    this.engagementCache$.next(null);
    this.ticketAnalyticsCache$.next(null);
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): { size: number; entries: string[] } {
    const entries: string[] = [];
    this.cache.forEach((value, key) => {
      const age = Date.now() - value.timestamp;
      const ageMinutes = Math.floor(age / 60000);
      entries.push(`${key} (${ageMinutes}m old)`);
    });

    return {
      size: this.cache.size,
      entries
    };
  }
}